import {
  toWSDateString,
  useModalOldContext,
  WSButton,
  WSElement,
  WSLoader,
  WSModalOld,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { IClientInvoice } from "@wingspanhq/payments/dist/interfaces";
import React, { useEffect, useState } from "react";
import { useGetGuestAccountNumbers } from "../../../query/users/mutations";
import { useAccount } from "../../../query/users/queries";
import { InfoRow } from "../InfoRow/InfoRow";

export const AUTHORIZE_ACH_PAYMENT_MODAL = "AUTHORIZE_ACH_PAYMENT_MODAL";

export const AuthorizeACHPaymentModal: React.FC = () => (
  <WSModalOld name={AUTHORIZE_ACH_PAYMENT_MODAL} size="S">
    {props => <AuthorizeModalBody {...props} />}
  </WSModalOld>
);

const AuthorizeModalBody: React.FC<{
  accountId?: string;
  clientInvoice: IClientInvoice;
  onSubmit: () => void;
  isLoading: boolean;
  plaidAccountId?: string;
  plaidPublicToken?: string;
}> = ({
  accountId,
  clientInvoice,
  onSubmit,
  isLoading,
  plaidAccountId,
  plaidPublicToken
}) => {
  const accountQuery = useAccount(accountId || "", {
    enabled: !!accountId
  });
  const account = accountQuery.data;
  const { closeModal } = useModalOldContext();
  const [plaidAccountNumber, setPlaidAccountNumber] = useState("");
  const [plaidRoutingNumber, setPlaidRoutingNumber] = useState("");
  const [getGuestAccountNumbers, getGuestAccountNumbersMeta] =
    useGetGuestAccountNumbers();

  useEffect(() => {
    const callback = async () => {
      if (
        plaidAccountId &&
        plaidPublicToken &&
        !plaidAccountNumber &&
        !plaidRoutingNumber
      ) {
        await getGuestAccountNumbers(
          {
            accountId: plaidAccountId,
            publicToken: plaidPublicToken
          },
          {
            onSuccess: numbers => {
              setPlaidAccountNumber(numbers.account);
              setPlaidRoutingNumber(numbers.routing || "");
            }
          }
        );
      }
    };

    callback();
  }, [plaidAccountId, plaidPublicToken]);

  return (
    <>
      <WSText mb="XL">
        By clicking the "Authorize" button/continuing with this transaction, I
        authorize Wingspan to debit the following amount from my bank account on
        the date shown below.
      </WSText>
      <WSElement mb="XL">
        {(accountId && accountQuery.isInitialLoading) ||
        getGuestAccountNumbersMeta.isLoading ? (
          <WSLoader.Spinner size="S" />
        ) : (
          <>
            <InfoRow
              longName
              label="Amount:"
              value={formatMoney(clientInvoice.amount, clientInvoice.currency)}
            />
            <InfoRow
              longName
              label="Date:"
              value={toWSDateString(new Date(), "monthDayYear")}
            />
            <InfoRow
              longName
              label="Routing #:"
              value={account?.numbers?.routing ?? plaidRoutingNumber ?? "–"}
            />
            <InfoRow
              longName
              label="Account #:"
              value={account?.numbers?.account ?? plaidAccountNumber ?? "–"}
            />
          </>
        )}
      </WSElement>
      <WSText mb="XL">
        Once I authorize this payment, I understand there cannot be any changes
        or corrections.
      </WSText>
      <WSText mb="XL">
        Transactions made after 4:00 PM ET or on a weekend or holiday will be
        processed the next business day. It is recommended that you print a copy
        of this authorization and maintain it for your records.
      </WSText>
      <WSButton
        fullWidth
        loading={isLoading}
        onClick={() => {
          closeModal(AUTHORIZE_ACH_PAYMENT_MODAL);
          onSubmit();
        }}
        name="authorize"
      >
        Authorize
      </WSButton>
    </>
  );
};
